import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faRobot, faSearch, faTimes, faUserShield } from '@fortawesome/free-solid-svg-icons';
import axios from '../../Api';
import SearchableSelect from '../../components/SearchableSelect';

const RespControleAiChecks = () => {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 15;
  const [stats, setStats] = useState({ total: 0, validated: 0, rejected: 0, hold: 0 });

  const emptyFilters = { survey_id: '', action: '', from_date: '', to_date: '', search: '' };
  const [filters, setFilters] = useState(emptyFilters);
  const [pending, setPending] = useState(emptyFilters);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const checkDismiss = useModalDismiss(() => setSelectedCheck(null), { enabled: !!selectedCheck });

  useEffect(() => {
    axios.get('/control/resp/surveys').then(r => setSurveys(r.data.surveys || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchChecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const fetchChecks = async () => {
    try {
      setLoading(true);
      const params = { page, per_page: perPage };
      if (filters.survey_id) params.survey_id = filters.survey_id;
      if (filters.action) params.action = filters.action;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;
      if (filters.search) params.search = filters.search;

      const r = await axios.get('/control/resp/ai/checks', { params });
      setChecks(r.data.data || []);
      setTotalPages(r.data.last_page || 1);
      setTotal(r.data.total || 0);
      if (r.data.statistics) setStats(r.data.statistics);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const apply = () => { setFilters(pending); setPage(1); };
  const reset = () => { setPending(emptyFilters); setFilters(emptyFilters); setPage(1); };

  const actionLabel = (a) => ({ validate: 'Validé', reject: 'Rejeté', hold: 'En attente' }[a] || a);
  const actionBadge = (a) => {
    const cls = { validate: 'bg-green-100 text-green-800', reject: 'bg-red-100 text-red-800', hold: 'bg-yellow-100 text-yellow-800' }[a] || 'bg-gray-100 text-gray-800';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${cls}`}>{actionLabel(a)}</span>;
  };

  const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR') : '-';

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faRobot} className="text-red-600 text-xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Contrôles IA effectués</h1>
            <p className="text-sm text-gray-600">Liste de tous les contrôles importés via IA.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-xs text-gray-500">Total</div>
            <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-xs text-gray-500">Validés</div>
            <div className="text-2xl font-bold text-green-600">{stats.validated}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-xs text-gray-500">Rejetés</div>
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-xs text-gray-500">En attente</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.hold}</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="mb-3 flex gap-2">
            <div className="relative flex-1">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={pending.search}
                onChange={(e) => setPending({ ...pending, search: e.target.value })}
                onKeyPress={(e) => { if (e.key === 'Enter') apply(); }}
                placeholder="Rechercher par ID soumission, commentaire, motif..."
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Action</label>
              <select
                value={pending.action}
                onChange={(e) => setPending({ ...pending, action: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">Toutes</option>
                <option value="validate">Validé</option>
                <option value="reject">Rejeté</option>
                <option value="hold">En attente</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Enquête</label>
              <SearchableSelect
                options={surveys}
                selectedValue={pending.survey_id ? parseInt(pending.survey_id) : null}
                onChange={(v) => setPending({ ...pending, survey_id: v ? String(v) : '' })}
                placeholder="-- Toutes --"
                searchPlaceholder="Rechercher..."
                getOptionLabel={(s) => s.title}
                getOptionMeta={(s) => s.limesurvey_id ? `ID: ${s.limesurvey_id}` : null}
                maxHeight="200px"
                triggerClassName="px-3 py-2"
                textClassName="text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date début</label>
              <input
                type="date"
                value={pending.from_date}
                onChange={(e) => setPending({ ...pending, from_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date fin</label>
              <input
                type="date"
                value={pending.to_date}
                onChange={(e) => setPending({ ...pending, to_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={apply} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700">Appliquer</button>
            <button onClick={reset} className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300">Réinitialiser</button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Chargement...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enquête</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Soumission</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contrôleur</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Raison</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flags</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {checks.length === 0 ? (
                    <tr><td colSpan="8" className="px-6 py-12 text-center text-gray-500">Aucun contrôle IA trouvé</td></tr>
                  ) : checks.map(c => {
                    const survey = c.assignment?.survey || c.submission?.survey || c.f2f_submission?.survey;
                    const responseId = c.submission?.response_id || c.f2f_submission?.response_id || c.assignment?.submission?.response_id || c.assignment?.f2f_submission?.response_id;
                    return (
                      <tr key={c.id} onClick={() => setSelectedCheck(c)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-4 py-3 text-sm text-gray-900">{fmt(c.checked_at)}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{survey?.title || 'N/A'}</div>
                          <div className="text-xs text-gray-500">ID: {survey?.limesurvey_id || 'N/A'}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">#{responseId || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">IA</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{c.rating ? `${c.rating}/5` : '-'}</td>
                        <td className="px-4 py-3">{actionBadge(c.action)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{c.raison || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs">
                          <div className="max-h-16 overflow-y-auto break-words">
                            {c.action === 'reject' ? (c.reject_reason || '-') : (c.comment || '-')}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between bg-white rounded-xl shadow-sm p-4">
            <div className="text-sm text-gray-700">
              {((page - 1) * perPage) + 1} – {Math.min(page * perPage, total)} sur {total}
            </div>
            <div className="flex gap-1">
              <button onClick={() => setPage(page - 1)} disabled={page === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button onClick={() => setPage(page + 1)} disabled={page === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedCheck && (() => {
        const c = selectedCheck;
        const survey = c.assignment?.survey || c.submission?.survey || c.f2f_submission?.survey;
        const responseId = c.submission?.response_id || c.f2f_submission?.response_id || c.assignment?.submission?.response_id || c.assignment?.f2f_submission?.response_id;
        const isF2f = !!c.f2f_submission_id;
        const f2fPerson = c.f2f_submission?.interviewer || c.f2f_submission?.supervisor;
        const personName = c.assignment?.interviewer?.name || c.assignment?.supervisor?.name || f2fPerson?.name;
        const personRef = c.assignment?.interviewer?.ref || c.assignment?.supervisor?.ref || f2fPerson?.ref;
        const personType = c.assignment?.supervisor_id || c.f2f_submission?.person_type === 'supervisor' ? 'Prestataire' : 'Enquêteur';
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" {...checkDismiss}>
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">IA</span>
                  <h2 className="text-xl font-bold text-gray-800">Détails du contrôle #{c.id}</h2>
                </div>
                <button onClick={() => setSelectedCheck(null)} className="text-gray-400 hover:text-gray-600">
                  <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Date de contrôle</div>
                  <div className="text-sm font-medium text-gray-900">{fmt(c.checked_at)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Action</div>
                  <div>{actionBadge(c.action)}</div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-xs text-gray-500 mb-0.5">Enquête</div>
                  <div className="text-sm font-medium text-gray-900">{survey?.title || 'N/A'}</div>
                  <div className="text-xs text-gray-500">ID LimeSurvey: {survey?.limesurvey_id || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">ID Soumission</div>
                  <div className="text-sm font-medium text-gray-900">#{responseId || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Type</div>
                  <div className="text-sm font-medium text-gray-900">{isF2f ? 'F2F' : 'CATI'}</div>
                </div>
                {personName && (
                  <div className="sm:col-span-2">
                    <div className="text-xs text-gray-500 mb-0.5">{personType}</div>
                    <div className="text-sm font-medium text-gray-900">{personName} {personRef && <span className="text-gray-500 ml-1">({personRef})</span>}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Note</div>
                  <div className="text-sm font-medium text-gray-900">{c.rating ? `${c.rating}/5 ★` : '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Raison</div>
                  <div className="text-sm font-medium text-gray-900">{c.raison || '-'}</div>
                </div>
                {c.action === 'reject' ? (
                  <div className="sm:col-span-2">
                    <div className="text-xs text-gray-500 mb-0.5">Motif de rejet</div>
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 break-words">{c.reject_reason || '-'}</div>
                  </div>
                ) : c.comment && (
                  <div className="sm:col-span-2">
                    <div className="text-xs text-gray-500 mb-0.5">Flags</div>
                    <div className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded p-2 break-words">{c.comment}</div>
                  </div>
                )}
              </div>

              {c.action === 'hold' && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <FontAwesomeIcon icon={faUserShield} className="text-yellow-500" />
                    Assigné à
                  </div>
                  {c.assignment?.controller ? (
                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <div className="text-sm font-medium text-blue-900">{c.assignment.controller.name}</div>
                      {c.assignment.controller.ref && (
                        <div className="text-xs text-blue-700">Réf: {c.assignment.controller.ref}</div>
                      )}
                      <div className="mt-1">
                        <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">Contrôleur</span>
                      </div>
                    </div>
                  ) : c.ai_assigned_to_user ? (
                    <div className="bg-purple-50 border border-purple-200 rounded p-3">
                      <div className="text-sm font-medium text-purple-900">{c.ai_assigned_to_user.name}</div>
                      {c.ai_assigned_to_user.email && (
                        <div className="text-xs text-purple-700">{c.ai_assigned_to_user.email}</div>
                      )}
                      <div className="mt-1">
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">Resp. Contrôle</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">Non assigné</div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default RespControleAiChecks;
